import React, { useEffect, useRef, useState } from "react";
import Navbar from "./Write_Navbar/Navbar";
import "./Write.scss";
import Scrapbook from "./Scrapbook/Scrapbook";
import Settings from "./Settings/Settings";
import config from "../../config/apiConfig";
import Cookies from "js-cookie";
import { useQuery } from "../../utils/hooks";
import Loader from "react-spinners/RingLoader";
import { RawDraftContentState } from "draft-js";
import TextUtil from "../../utils/textUtil";
import { RouteComponentProps } from "react-router";
import ReactGA from "react-ga";

export type SettingsType = {
    cover: string;
    title: string;
    description: string;
    characters: string[];
    tags: string[];
    genre: string;
    audience: string;
    language: string;
    copyrights: string;
    mature: boolean;
    viability: boolean;
    cover_image?: any;
    protagonist: string;
    otherProtagonists: string;
    protagonistPronoun: string;
    protagonistAttr: string;
    protagonistPersonality: string;
    protagonistGoal: string;
    goalType: string;
    storyEnding: string;
    goalSuccess: string;
    protagonistScoresIntroverted: number;
    protagonistScoresLikable: number;
    protagonistScoresFocused: number;
    protagonistScoresEmotional: number;
    protagonistScoresImprovised: number;
};

export type Parts = {
    id: string;
    title: string;
    body: {
        raw?: RawDraftContentState;
        html?: string;
    };
};

const Write: React.FC<RouteComponentProps> = (props) => {
    const query = useQuery();
    const id = query.get("story_id");
    const chapterId = query.get("chapter_id");
    const [menu, setMenu] = useState<number>(id !== null ? 0 : 1);
    const [selectedIndex, setSelectedIndex] = useState<number>(0);
    const [published, setPublished] = useState(false);
    const [values, setValues] = useState<SettingsType>({
        cover: "",
        title: "",
        description: "",
        characters: [],
        tags: [],
        genre: "",
        audience: "",
        language: "",
        copyrights: "All Rights Reserved",
        mature: true,
        viability: false,
        protagonist: "",
        otherProtagonists: "",
        protagonistPronoun: "",
        protagonistAttr: "",
        protagonistPersonality: "",
        protagonistGoal: "",
        goalType: "",
        storyEnding: "",
        goalSuccess: "",
        protagonistScoresIntroverted: 0,
        protagonistScoresLikable: 0,
        protagonistScoresFocused: 0,
        protagonistScoresEmotional: 0,
        protagonistScoresImprovised: 0,
    });
    const [storyId, setStoryId] = useState<string>(id ?? "");
    const [parts, setParts] = useState<Parts[]>([
        {
            id: "",
            title: "",
            body: {},
        },
    ]);
    const [isLoading, setIsLoading] = useState(false);
    const [scrapVisible, setScrapVisible] = useState(true);

    useEffect(() => {
        if (storyId === "") return;
        const formData = new FormData();
        formData.append("session-token", Cookies.get("token") ?? "");
        formData.append("token", config.token);
        const requestParams = {
            method: "POST",
            body: formData,
        };
        const promise1 = fetch(
            config.BASE_URL + "/v1/get/writingtool/story/" + storyId + "/info",
            requestParams
        )
            .then((res) => res.json())
            .then((res) => {
                setValues({
                    cover: res.data.cover_image,
                    title: res.data.title,
                    description: res.data.short_description,
                    characters: res.data.characters,
                    tags: res.data.tags,
                    genre: res.data.genres,
                    audience: res.data.target_audience,
                    language: res.data.language,
                    copyrights: res.data.copyright,
                    mature: res.data.rating === "mature",
                    viability: res.data.viability === "private",
                    protagonist: res.data.sub_meta?.protagonist_name,
                    otherProtagonists:
                        res.data.sub_meta?.other_protagonist_name,
                    protagonistPronoun: res.data.sub_meta?.protagonist_pronoun,
                    protagonistAttr: res.data.sub_meta?.protagonist_attributes,
                    protagonistPersonality:
                        res.data.sub_meta?.protagonist_personality,
                    protagonistGoal: res.data.sub_meta?.protagonist_goal,
                    goalType: res.data.sub_meta?.goal_type,
                    storyEnding: res.data.sub_meta?.end_of_story,
                    goalSuccess: res.data.sub_meta?.protagonist_success,
                    protagonistScoresIntroverted:
                        res.data.sub_meta?.protagonist_scores?.introverted ?? 0,
                    protagonistScoresLikable:
                        res.data.sub_meta?.protagonist_scores?.likable ?? 0,
                    protagonistScoresFocused:
                        res.data.sub_meta?.protagonist_scores?.focused ?? 0,
                    protagonistScoresEmotional:
                        res.data.sub_meta?.protagonist_scores?.emotional ?? 0,
                    protagonistScoresImprovised:
                        res.data.sub_meta?.protagonist_scores?.improvised ?? 0,
                });
                setPublished(res.data.status === "published");
            });
        const promise2 = fetch(
            config.BASE_URL +
                "/v1/get/writingtool/story/" +
                storyId +
                "/chapters",
            requestParams
        )
            .then((res) => res.json())
            .then((res) => {
                if (res.data.length !== 0) {
                    const data = (res.data as any[]).map((prt) => ({
                        ...prt,
                        body: {},
                    }));
                    if (chapterId === "new") {
                        data.push({
                            id: "",
                            title: "",
                            body: {},
                        });
                    }
                    setParts(data);
                }
                if (!chapterId) return;
                else if (chapterId === "new") {
                    setTimeout(() => {
                        setSelectedIndex(res.data.length);
                    }, 500);
                } else {
                    const part = res.data.find((p: any) => p.id === chapterId);
                    if (!!part) {
                        const idx = res.data.indexOf(part);
                        setSelectedIndex(idx);
                    }
                }
            });
        setIsLoading(true);
        Promise.all([promise1, promise2]).finally(() => setIsLoading(false));
    }, []);

    const addNewStory = () => {
        setParts([
            ...parts,
            {
                id: "",
                title: "",
                body: {},
            },
        ]);
        setSelectedIndex(parts.length);
    };

    const saveStory = (formData: FormData) => {
        const requestParams = {
            method: "POST",
            body: formData,
        };
        return fetch(
            config.BASE_URL + "/v1/put/writingtool/story/new",
            requestParams
        )
            .then((res) => res.json())
            .then((res) => setStoryId(res.data.story_id));
    };

    const updateStory = (formData: FormData) => {
        const requestParams = {
            method: "POST",
            body: formData,
        };
        return fetch(
            config.BASE_URL + "/v1/put/writingtool/story/update",
            requestParams
        ).then(() => {});
    };

    const savePart = (formData: FormData) => {
        const requestParams = {
            method: "POST",
            body: formData,
        };
        return fetch(
            config.BASE_URL +
                "/v1/put/writingtool/story/" +
                storyId +
                "/chapter/new",
            requestParams
        )
            .then((res) => res.json())
            .then((res) => {
                const newParts = [...parts];
                newParts[selectedIndex].id = res.data.chapter_id;
                setParts([...newParts]);
            });
    };

    const updatePart = (formData: FormData) => {
        const requestParams = {
            method: "POST",
            body: formData,
        };
        return fetch(
            config.BASE_URL +
                "/v1/put/writingtool/story/" +
                storyId +
                "/chapter/update",
            requestParams
        ).then(() => {});
    };

    const handleSave = async (lazySave = false) => {
        if (!lazySave) setIsLoading(true);
        const formData = new FormData();
        formData.append("session-token", Cookies.get("token") ?? "");
        formData.append("token", config.token);

        try {
            if (menu === 1 && !lazySave) {
                const data = {
                    title: values.title,
                    short_description: values.description,
                    language: values.language,
                    genres: values.genre,
                    rating: values.mature ? "mature" : "all",
                    copyright: values.copyrights,
                    target_audience: values.audience,
                    characters: values.characters,
                    tags: values.tags,
                    viability: values.viability ? "private" : "public",
                    protagonist_name: values.protagonist,
                    other_protagonist_name: values.otherProtagonists,
                    protagonist_pronoun: values.protagonistPronoun,
                    protagonist_attributes: values.protagonistAttr,
                    protagonist_personality: values.protagonistPersonality,
                    protagonist_goal: values.protagonistGoal,
                    goal_type: values.goalType,
                    end_of_story: values.storyEnding,
                    protagonist_success: values.goalSuccess,
                    protagonist_scores: {
                        introverted: values.protagonistScoresIntroverted,
                        likable: values.protagonistScoresLikable,
                        focused: values.protagonistScoresFocused,
                        emotional: values.protagonistScoresEmotional,
                        improvised: values.protagonistScoresIntroverted,
                    },
                };
                if (
                    data.title === "" ||
                    data.short_description === "" ||
                    data.target_audience === "" ||
                    data.genres === "" ||
                    data.language === "" ||
                    data.characters.length < 1
                ) {
                    return alert("Please fill required fields!");
                }
                formData.append("payload", JSON.stringify(data));
                if (values.cover_image)
                    formData.append(
                        "cover_image",
                        values.cover_image,
                        values.title.replaceAll(/\s|\n|/g, "") + ".jpg"
                    );
                else if (values.cover)
                    formData.append("image_url", values.cover);
                if (storyId === "") {
                    await saveStory(formData);
                } else {
                    formData.append("story_id", storyId);
                    await updateStory(formData);
                }
                setMenu(0);
            } else {
                const part = parts[selectedIndex];
                if (part !== undefined) {
                    const data = {
                        title: part.title,
                        chapter_number: selectedIndex + 1,
                        body: {
                            raw: JSON.stringify(part.body.raw),
                            html: part.body.html,
                        },
                    };
                    formData.append("payload", JSON.stringify(data));

                    if (part.id === "") {
                        await savePart(formData);
                    } else {
                        formData.append("chapter_id", part.id);
                        await updatePart(formData);
                    }
                    // setTimeout(() => {
                    //     setIsLoading(false);
                    //     setTimeout(() => window.alert("Chapter saved!!"), 200);
                    // }, 300);
                }
            }
        } finally {
            if (!lazySave) {
                setTimeout(() => setIsLoading(false), 500);
            }
            // if (published) {
            //     props.history.push("/story/my?tab=1");
            // } else {
            //     props.history.push("/story/my");
            // }
        }
    };

    const handlePublish = async () => {
        if (storyId === "") return;
        if (!window.confirm("Are you sure you want to publish the story?"))
            return;
        const formData = new FormData();
        formData.append("session-token", Cookies.get("token") ?? "");
        formData.append("token", config.token);
        const requestParams = {
            method: "POST",
            body: formData,
        };
        setIsLoading(true);
        await fetch(
            config.BASE_URL +
                "/v1/put/writingtool/story/" +
                storyId +
                "/published",
            requestParams
        ).finally(() => {
            setIsLoading(false);
            props.history.push("/writer/story/my?tab=1");
        });
    };

    function hanldeMenuChange(menu: number) {
        setMenu(menu);
        if (menu === 0 && !scrapVisible) {
            setScrapVisible(true);
        }
    }

    return (
        <div className="write">
            <Navbar
                menu={menu}
                storyId={storyId}
                handleSave={handleSave}
                handlePublish={handlePublish}
                published={published}
                setMenu={hanldeMenuChange}
                title={values.title}
                parts={parts}
                selectedIndex={selectedIndex}
                setSelectedIndex={(val: number) => setSelectedIndex(val)}
                addNewStory={addNewStory}
            />
            <div className="body">
                {menu == 0 ? (
                    <Scrapbook
                        storyId={storyId}
                        scrapVisible={scrapVisible}
                        closeScrap={() => setScrapVisible(false)}
                        parts={parts[selectedIndex]}
                        setParts={(val: Parts) => {
                            let newParts = [...parts];
                            newParts[selectedIndex] = val;
                            setParts(newParts);
                        }}
                    />
                ) : (
                    <Settings
                        values={values}
                        dismissible={storyId !== ""}
                        setValues={(val: SettingsType) => setValues(val)}
                        goToScrapbook={() => setMenu(0)}
                    />
                )}
            </div>
            {isLoading && (
                <div className="backdrop">
                    <Loader color="pink" />
                </div>
            )}
        </div>
    );
};

export default Write;
